import ChevronDownIcon from '@assets/icons/chevron-down.svg';
import React, { useRef, useState } from 'react';
import { BaseActionSheet } from './base-action-sheet';
import { createStyles } from '@/contexts/theme-context';
import { FONTS, TYPOGRAPHY } from '@/styles';
import { INPUT_HEIGHT } from './override-input';
import {
  Platform,
  StyleProp,
  Text,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { Picker as RNPicker } from '@react-native-picker/picker';

const INPUT_BORDER_WIDTH = 2;
const PICKER_ARROW_HEIGHT = 7;
// Based on SVG proportions
const PICKER_ARROW_WIDTH = PICKER_ARROW_HEIGHT * 2;

const PICKER_HIT_SLOP = {
  bottom: 8,
  left: 4,
  right: 4,
  top: 8,
};

interface PickerProps {
  enabled?: boolean;
  onSelect: (option: string) => void;
  options: string[];
  selectedValue: string;
  style?: StyleProp<ViewStyle>;
}

export const Picker: React.FC<PickerProps> = ({
  enabled = true,
  onSelect,
  options,
  selectedValue,
  style,
}) => {
  const [styles, theme] = useStyles();
  const [showPickerActionSheet, setShowPickerActionSheet] = useState(false);
  const pickerRef = useRef<RNPicker<string>>(null);

  const onFocus = () => {
    if (Platform.OS === 'ios') {
      setShowPickerActionSheet(true);
    } else {
      pickerRef.current?.focus();
    }
  };

  const onValueChange = (value: string) => {
    onSelect(value);

    if (Platform.OS !== 'ios') {
      pickerRef.current?.blur();
    }
  };

  const PickerComponent = (
    <RNPicker
      dropdownIconColor={theme.colors.white}
      dropdownIconRippleColor={theme.colors.white}
      enabled={enabled}
      onValueChange={onValueChange}
      ref={pickerRef}
      selectedValue={selectedValue}
      style={
        Platform.OS === 'web'
          ? [
              TYPOGRAPHY.small,
              styles.pickerWeb,
              !enabled ? styles.disabledText : null,
            ]
          : null
      }
    >
      {options.map((opt) => (
        <RNPicker.Item key={opt} label={opt} value={opt} />
      ))}
    </RNPicker>
  );

  return (
    <>
      {Platform.OS !== 'web' ? (
        <TouchableOpacity
          disabled={!enabled}
          hitSlop={PICKER_HIT_SLOP}
          onPress={onFocus}
          style={[styles.container, style]}
        >
          <Text
            style={[
              TYPOGRAPHY.small,
              styles.selectedText,
              !enabled && styles.disabledText,
            ]}
          >
            {selectedValue}
          </Text>
        </TouchableOpacity>
      ) : null}
      {Platform.select({
        android: <View style={styles.android}>{PickerComponent}</View>,
        ios: (
          <BaseActionSheet
            isVisible={showPickerActionSheet}
            onClose={() => setShowPickerActionSheet(false)}
          >
            {PickerComponent}
          </BaseActionSheet>
        ),
        web: (
          <View style={style}>
            {PickerComponent}
            <View style={styles.pickerArrowIcon}>
              <ChevronDownIcon
                fill={theme.colors.primary}
                height={PICKER_ARROW_HEIGHT}
                width={PICKER_ARROW_WIDTH}
              />
            </View>
          </View>
        ),
      })}
    </>
  );
};

const useStyles = createStyles((theme) => ({
  android: {
    // Prevent double render on Android due to ref
    display: 'none',
  },
  container: {
    backgroundColor: theme.colors.white,
    borderColor: theme.colors.stroke,
    borderRadius: 4,
    borderWidth: INPUT_BORDER_WIDTH,
    height: INPUT_HEIGHT,
    justifyContent: 'center',
  },
  disabledText: {
    color: theme.colors.inactiveText,
  },
  pickerArrowIcon: {
    marginTop: -PICKER_ARROW_HEIGHT / 2,
    position: 'absolute',
    right: 16,
    top: '50%',
  },
  pickerWeb: {
    ...FONTS.primarySemiBold,
    '-moz-appearance': 'none',
    '-webkit-appearance': 'none',
    borderColor: theme.colors.stroke,
    borderRadius: 4,
    borderWidth: INPUT_BORDER_WIDTH,
    height: '100%',
    outlineColor: theme.colors.accent,
    paddingLeft: 16,
    paddingRight: PICKER_ARROW_WIDTH + 32,
  },
  selectedText: {
    ...FONTS.primarySemiBold,
    paddingHorizontal: 16,
  },
}));
